import type { ButtonHTMLAttributes, ReactNode } from 'react';
import type {
  ClassificationStatus,
  DocumentStatus,
  OcrProgress,
  OcrStatus,
  PaperworkKind,
  ProcessingStatus,
  RedactionStatus,
  RoomDocument,
} from '../api/types';
import { cn } from '../lib/utils';
import { Icon, type IconName } from './icons';

/** The status palette (DataRoom UI st-* triplets). Every fg/bg pair passes 4.5:1
 * in both themes; tones that must be told apart also differ in icon and word,
 * never hue alone. */
export type ChipTone =
  | 'filed'
  | 'check'
  | 'processing'
  | 'error'
  | 'loi'
  | 'classified'
  | 'certified'
  | 'recert'
  | 'na'
  | 'neutral';

const TONE: Record<ChipTone, string> = {
  filed: 'border-st-filed-line bg-st-filed text-st-filed-fg',
  check: 'border-st-check-line bg-st-check text-st-check-fg',
  processing: 'border-st-processing-line bg-st-processing text-st-processing-fg',
  error: 'border-st-error-line bg-st-error text-st-error-fg',
  loi: 'border-st-loi-line bg-st-loi text-st-loi-fg',
  classified: 'border-st-classified-line bg-st-classified text-st-classified-fg',
  certified: 'border-st-certified-line bg-st-certified text-st-certified-fg',
  recert: 'border-st-recert-line bg-st-recert text-st-recert-fg',
  na: 'border-dashed border-line-strong bg-transparent text-st-na-fg',
  neutral: 'border-line bg-surface-2 text-ink-2',
};

export const chipBaseClass =
  'inline-flex h-6 shrink-0 items-center gap-1 whitespace-nowrap rounded-pill border pl-2 pr-2.5 text-xs font-semibold leading-4';

export function chipClass(tone: ChipTone): string {
  return cn(chipBaseClass, TONE[tone]);
}

/** A status chip: word + icon + colour. Never wraps; keep labels to ≤3 words. */
export function Chip({
  tone,
  icon,
  spin,
  className,
  children,
}: {
  tone: ChipTone;
  icon?: IconName;
  /** Spin the icon (Processing). Stops under prefers-reduced-motion. */
  spin?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span className={cn(chipClass(tone), className)}>
      {icon ? <Icon name={icon} size={12} className={spin ? 'animate-spin' : undefined} /> : null}
      {children}
    </span>
  );
}

/** ONE status per document in the intermediary's data room. */
export type DocumentStatusKey =
  | 'processing'
  | 'check'
  | 'filed'
  | 'error'
  | 'loi'
  | 'loi_counter'
  | 'master_nda'
  | 'agreement'
  | 'unpacked'
  // Low confidence, when the server serves the processing step (admin/seller).
  | 'needs_filing'
  // The same, for a document that already rests in a folder (a parent): the
  // row visibly sits somewhere, so "Needs filing" would contradict it.
  | 'choose_subfolder';

export const DOCUMENT_STATUS: Record<
  DocumentStatusKey,
  { label: string; tone: ChipTone; icon: IconName; description: string }
> = {
  processing: {
    label: 'Classifying',
    tone: 'processing',
    icon: 'spinner',
    description: 'The AI is reading and filing this document.',
  },
  check: {
    label: 'Check AI',
    tone: 'check',
    icon: 'eye',
    description: 'The AI filed this with low confidence — a person should check it.',
  },
  filed: {
    label: 'Filed',
    tone: 'filed',
    icon: 'folder',
    description: 'Filed to its category.',
  },
  error: {
    label: "Couldn't file",
    tone: 'error',
    icon: 'alert',
    description: 'The AI could not file this document — choose its category by hand.',
  },
  loi: {
    label: 'Buyer LOI',
    tone: 'loi',
    icon: 'pen',
    description: "A buyer's letter of intent — deal paperwork, kept out of the DD library.",
  },
  loi_counter: {
    label: 'LOI counter',
    tone: 'loi',
    icon: 'pen',
    description:
      "A red-line counter to a buyer's LOI — deal paperwork, kept out of the DD library.",
  },
  master_nda: {
    label: 'Master NDA',
    tone: 'loi',
    icon: 'pen',
    description: 'The NDA buyers sign for this room — deal paperwork, kept out of the DD library.',
  },
  agreement: {
    label: 'Agreement',
    tone: 'loi',
    icon: 'pen',
    description: 'Issued for signature — deal paperwork, kept out of the DD library.',
  },
  unpacked: {
    label: 'Unpacked',
    tone: 'neutral',
    icon: 'archive',
    description: 'A zip or saved email — each file inside was filed as its own document.',
  },
  needs_filing: {
    label: 'Needs filing',
    tone: 'check',
    icon: 'folder',
    description: 'The AI was not sure of the category — a person should choose where it is filed.',
  },
  choose_subfolder: {
    label: 'Choose a subfolder',
    tone: 'check',
    icon: 'folder',
    description:
      'The AI filed this in a folder but was not sure of the subfolder — a person should choose it.',
  },
};

/** The in-progress chips (their icon spins). */
const SPINNING: ReadonlySet<DocumentStatusKey> = new Set(['processing']);

/** The chip for each kind of deal paperwork. */
const PAPERWORK_STATUS: Record<PaperworkKind, DocumentStatusKey> = {
  master_nda: 'master_nda',
  loi: 'loi',
  loi_redline: 'loi_counter',
  signing: 'agreement',
};

/**
 * The ONE primary status a document shows: its FILING result (owner direction
 * 2026-10-12: "categorization, and then an OCR and redaction status"). The AI
 * classification state wins when present; legacy rows without one fall back to
 * the categorization status. Classifying → Filed / Needs filing / Couldn't file,
 * or which deal paperwork it is. A filed document reads "Filed" whatever OCR
 * and redaction are doing — those are the separate, smaller {@link FileIndicatorsView}.
 *
 * `processing` (the listing's `processingStatus`) only says the server serves
 * the processing step (the room admin / bound seller): a low-confidence filing
 * is then "Needs filing" rather than the older "Check AI". Its value — known or
 * not — is otherwise ignored here.
 */
export function documentStatusKey(
  classification: ClassificationStatus | undefined,
  status: DocumentStatus,
  paperworkKind?: PaperworkKind | null,
  /** The document is an unpacked zip / saved email (the listing's `archive`). */
  unpacked?: boolean,
  /** The document's processing step (the listing's `processingStatus`). */
  processing?: ProcessingStatus | null,
  /** The document already rests in a folder (its `categoryPath` is not empty). */
  inFolder?: boolean,
): DocumentStatusKey {
  // Never classified by design either, but it is not deal paperwork.
  if (unpacked && classification === 'not_classifiable') return 'unpacked';
  switch (classification) {
    case 'not_classifiable':
      // Deal paperwork: name WHICH paperwork (a master NDA is not a buyer LOI).
      // Without a kind (older servers) it reads as an LOI, as before.
      return paperworkKind ? PAPERWORK_STATUS[paperworkKind] : 'loi';
    case 'processing':
      return 'processing';
    case 'failed':
      return 'error';
    case 'low_confidence':
      if (!processing) return 'check';
      // A document resting in a PARENT folder is not "unfiled": it needs its subfolder.
      return inFolder ? 'choose_subfolder' : 'needs_filing';
    case 'classified':
      return status === 'processing' ? 'processing' : 'filed';
    default:
      return status === 'processing' ? 'processing' : status === 'needs_review' ? 'check' : 'filed';
  }
}

/**
 * The two small per-file indicators beside the filing chip (owner direction
 * 2026-10-12): OCR and redaction run in the background and never hold anything
 * up, so they are muted words with an icon — except where a person must act
 * (redaction needs review, OCR or redaction failed), which keep a coloured pill.
 * Nothing is shown when there is nothing to say (no OCR needed, redaction off).
 */
type IndicatorTone = 'muted' | 'check' | 'error';
interface IndicatorSpec {
  label: string;
  icon: IconName;
  spin: boolean;
  tone: IndicatorTone;
  description: string;
}

export const OCR_INDICATOR: Record<Exclude<OcrStatus, 'none'>, IndicatorSpec> = {
  pending: {
    label: 'OCR pending',
    icon: 'spinner',
    spin: true,
    tone: 'muted',
    description: 'Reading the scanned pages (OCR) in the background so they are searchable.',
  },
  complete: {
    label: 'OCR complete',
    icon: 'check',
    spin: false,
    tone: 'muted',
    description: 'The scanned pages were read (OCR); the document is fully searchable.',
  },
  failed: {
    label: 'OCR failed',
    icon: 'alert',
    spin: false,
    tone: 'error',
    description:
      'The scanned pages could not be read — search will miss their text. Ask for a clearer copy.',
  },
};

export const REDACTION_INDICATOR: Record<Exclude<RedactionStatus, 'off'>, IndicatorSpec> = {
  pending: {
    label: 'Redaction pending',
    icon: 'spinner',
    spin: true,
    tone: 'muted',
    description: 'Checking for personal data to redact, in the background (after OCR, if any).',
  },
  complete: {
    label: 'Redaction complete',
    icon: 'check',
    spin: false,
    tone: 'muted',
    description:
      'The redaction check is finished: the verified copy is ready, or nothing needed hiding.',
  },
  needs_review: {
    label: 'Redaction: needs review',
    icon: 'eye',
    spin: false,
    tone: 'check',
    description:
      'Some redactions are uncertain, or the copy could not be made safely — a person must review it.',
  },
  unsupported: {
    label: 'Redaction n/a',
    icon: 'minus',
    spin: false,
    tone: 'muted',
    description: "This file type can't be redacted automatically yet.",
  },
  failed: {
    label: 'Redaction failed',
    icon: 'alert',
    spin: false,
    tone: 'error',
    description: 'The redaction check could not finish — a person should look at this document.',
  },
};

export interface FileIndicators {
  ocr: Exclude<OcrStatus, 'none'> | null;
  ocrProgress: OcrProgress | null;
  redaction: Exclude<RedactionStatus, 'off'> | null;
}

/** Step → indicators, for a server that serves `processingStatus` but not the fields yet. */
const FROM_STEP: Partial<Record<string, Pick<FileIndicators, 'ocr' | 'redaction'>>> = {
  ocr: { ocr: 'pending', redaction: 'pending' },
  redaction_check: { ocr: null, redaction: 'pending' },
  needs_review: { ocr: null, redaction: 'needs_review' },
};

/**
 * A document's two indicators: the listing's `ocrStatus` / `ocrProgress` /
 * `redactionStatus` (room admin and bound seller only — buyers get null), or —
 * from a server that does not serve them yet — read off `processingStatus`.
 * Null for "nothing to show", an absent value, or a value this app does not know.
 */
export function fileIndicatorsOf(
  doc: Pick<
    RoomDocument,
    'classificationStatus' | 'processingStatus' | 'ocrStatus' | 'ocrProgress' | 'redactionStatus'
  >,
): FileIndicators {
  if (doc.ocrStatus === undefined && doc.redactionStatus === undefined) {
    const step =
      doc.classificationStatus === 'classified' && doc.processingStatus
        ? FROM_STEP[doc.processingStatus]
        : undefined;
    return { ocr: step?.ocr ?? null, ocrProgress: null, redaction: step?.redaction ?? null };
  }
  const ocr = doc.ocrStatus && doc.ocrStatus in OCR_INDICATOR ? doc.ocrStatus : null;
  const redaction =
    doc.redactionStatus && doc.redactionStatus in REDACTION_INDICATOR ? doc.redactionStatus : null;
  return {
    ocr: ocr as FileIndicators['ocr'],
    ocrProgress: ocr === 'pending' ? (doc.ocrProgress ?? null) : null,
    redaction: redaction as FileIndicators['redaction'],
  };
}

function Indicator({ spec, label }: { spec: IndicatorSpec; label?: string }) {
  const icon = (
    <Icon name={spec.icon} size={11} className={spec.spin ? 'animate-spin' : undefined} />
  );
  const text = label ?? spec.label;
  if (spec.tone === 'muted') {
    return (
      <span
        className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-[11px] leading-4 text-ink-3"
        title={spec.description}
      >
        {icon}
        {text}
      </span>
    );
  }
  return (
    <span
      className={cn(
        'inline-flex h-5 shrink-0 items-center gap-1 whitespace-nowrap rounded-pill border px-1.5 text-[11px] font-semibold leading-4',
        spec.tone === 'check'
          ? 'border-st-check-line bg-st-check text-st-check-fg'
          : 'border-st-error-line bg-st-error text-st-error-fg',
      )}
      title={spec.description}
    >
      {icon}
      {text}
    </span>
  );
}

/** Redaction states a person resolves on the review screen. */
const REVIEWABLE_REDACTION: ReadonlySet<string> = new Set(['needs_review', 'failed']);

/** The redaction indicator as the way in to the review screen. */
function ReviewRedactionButton({
  spec,
  onClick,
  subject,
}: {
  spec: IndicatorSpec;
  onClick: () => void;
  subject?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={spec.description}
      className={cn(
        'inline-flex h-6 shrink-0 items-center gap-1 whitespace-nowrap rounded-pill border px-2 text-[11px] font-semibold leading-4 transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        spec.tone === 'error'
          ? 'border-st-error-line bg-st-error text-st-error-fg'
          : 'border-st-check-line bg-st-check text-st-check-fg',
      )}
    >
      <Icon name="eye" size={11} />
      Review redaction
      {subject ? <span className="sr-only"> of {subject}</span> : null}
    </button>
  );
}

/** The small OCR + redaction indicators of one file (nothing when there is nothing to say). */
export function FileIndicatorsView({
  indicators,
  className,
  onReviewRedaction,
  subject,
  redactionAside,
}: {
  indicators: FileIndicators;
  className?: string;
  /**
   * Where a person must act on the redaction (needs review, or failed), the
   * indicator becomes a "Review redaction" button that calls this (owner,
   * 2026-10-15: "there is nowhere to review the redaction").
   */
  onReviewRedaction?: () => void;
  /** The document's name, appended to the button for assistive tech. */
  subject?: string;
  /** A "?" help tip shown after the redaction indicator (when there is one). */
  redactionAside?: ReactNode;
}) {
  const { ocr, ocrProgress, redaction } = indicators;
  if (!ocr && !redaction) return null;
  return (
    <span className={cn('inline-flex flex-wrap items-center gap-x-2 gap-y-1', className)}>
      {ocr ? (
        <Indicator
          spec={OCR_INDICATOR[ocr]}
          label={
            ocr === 'pending' && ocrProgress
              ? `OCR ${ocrProgress.pagesDone}/${ocrProgress.pageCount}`
              : undefined
          }
        />
      ) : null}
      {redaction && onReviewRedaction && REVIEWABLE_REDACTION.has(redaction) ? (
        <ReviewRedactionButton
          spec={REDACTION_INDICATOR[redaction]}
          onClick={onReviewRedaction}
          subject={subject}
        />
      ) : redaction ? (
        <Indicator spec={REDACTION_INDICATOR[redaction]} />
      ) : null}
      {redaction ? redactionAside : null}
    </span>
  );
}

export interface StatusChipProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'onClick'
> {
  status: DocumentStatusKey;
  /** When set the chip is a button — e.g. Check AI opens the review at that
   * document. */
  onClick?: () => void;
  /** What clicking does ("Review the filing of X") — appended for assistive tech
   * after the visible word, and the tooltip. */
  actionLabel?: string;
}

/** The document status chip (fixed labels — consumers pass a key, not words). */
export function StatusChip({ status, onClick, actionLabel, className, ...rest }: StatusChipProps) {
  const spec = DOCUMENT_STATUS[status];
  const content = (
    <>
      <Icon
        name={spec.icon}
        size={12}
        className={SPINNING.has(status) ? 'animate-spin' : undefined}
      />
      {spec.label}
    </>
  );
  if (!onClick) {
    return (
      <span className={cn(chipClass(spec.tone), className)} title={spec.description}>
        {content}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      title={actionLabel ?? spec.description}
      className={cn(
        chipClass(spec.tone),
        'cursor-pointer hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
        className,
      )}
      {...rest}
    >
      {content}
      <Icon name="chevronRight" size={12} />
      {actionLabel ? <span className="sr-only">{` — ${actionLabel}`}</span> : null}
    </button>
  );
}
